import { useEffect, useRef, useState, type ChangeEvent, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { workspacePath } from "../lib/workspaces";
import { Sparkles, Trash2, Loader2 } from "lucide-react";
import { apiGet, apiPatch, apiDelete, apiUpload } from "../lib/api";

interface TenantSettings {
  name: string;
  contactEmail: string;
  contactPhone: string | null;
  plan: string;
  retentionDays: number;
  logoUrl: string | null;
  brandingEnabled: boolean;
}

export function Settings() {
  const [settings, setSettings] = useState<TenantSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState({
    name: "",
    contactEmail: "",
    contactPhone: "",
    retentionDays: 0,
  });

  useEffect(() => {
    apiGet<TenantSettings>("/settings").then((res) => {
      if (res.success && res.data) {
        const d = res.data;
        setSettings(d);
        setForm({
          name: d.name,
          contactEmail: d.contactEmail,
          contactPhone: d.contactPhone || "",
          retentionDays: d.retentionDays,
        });
      }
      setLoading(false);
    });
  }, []);

  function update(field: string, value: string | number) {
    setForm((f) => ({ ...f, [field]: value }));
    setSaved(false);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSaved(false);

    const res = await apiPatch("/settings", {
      name: form.name,
      contactEmail: form.contactEmail,
      contactPhone: form.contactPhone || undefined,
      retentionDays: form.retentionDays,
    });

    if (res.success) {
      setSaved(true);
    } else {
      setError(res.error?.message || "Failed to save");
    }
    setSaving(false);
  }

  if (loading) return <div className="p-6 text-stone-500">Loading...</div>;

  return (
    <div className="p-4 sm:p-6 max-w-2xl">
      <h1 className="text-xl sm:text-2xl font-semibold text-stone-900 mb-4 sm:mb-6 font-display">Settings</h1>

      {settings?.brandingEnabled ? (
        <BrandingSection initialLogoUrl={settings.logoUrl} />
      ) : (
        <BrandingUpgradeGate />
      )}

      <div className="bg-white rounded-xl border border-stone-200 p-6">
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Business Name</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              required
              className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Contact Email</label>
            <input
              type="email"
              value={form.contactEmail}
              onChange={(e) => update("contactEmail", e.target.value)}
              required
              className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Contact Phone</label>
            <input
              type="tel"
              value={form.contactPhone}
              onChange={(e) => update("contactPhone", e.target.value)}
              className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Data Retention (days)
            </label>
            <input
              type="number"
              min={0}
              value={form.retentionDays}
              onChange={(e) => update("retentionDays", Number(e.target.value))}
              className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
            />
            <p className="text-xs text-stone-400 mt-1">
              Set to 0 to retain records indefinitely. If set, records older than this will be automatically purged nightly.
            </p>
          </div>

          <div className="pt-2 border-t border-stone-100">
            <div className="flex items-center gap-3">
              <span className="text-sm text-stone-500">Plan:</span>
              <span className="px-2.5 py-0.5 text-xs font-medium bg-brand-50 text-brand-700 rounded-full">
                {settings?.plan || "FREE"}
              </span>
            </div>
          </div>

          {error && <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</p>}
          {saved && <p className="text-sm text-green-600 bg-green-50 px-3 py-2 rounded-lg">Settings saved.</p>}

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-brand-500 text-white text-sm font-medium rounded-lg hover:bg-brand-700 disabled:opacity-50 transition-colors"
            >
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Branding ──

const ACCEPTED_LOGO_TYPES = "image/png,image/jpeg,image/webp,image/svg+xml";

function BrandingSection({ initialLogoUrl }: { initialLogoUrl: string | null }) {
  const [logoUrl, setLogoUrl] = useState(initialLogoUrl);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  async function handleUpload(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setError(null);

    const formData = new FormData();
    formData.append("logo", file);
    const res = await apiUpload<{ logoUrl: string }>("/logo", formData);

    if (res.success && res.data) setLogoUrl(res.data.logoUrl);
    else setError(res.error?.message || "Failed to upload logo");

    setBusy(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  async function handleRemove() {
    setBusy(true);
    setError(null);
    const res = await apiDelete("/logo");
    if (res.success) setLogoUrl(null);
    else setError(res.error?.message || "Failed to remove logo");
    setBusy(false);
  }

  return (
    <div className="bg-white rounded-xl border border-stone-200 p-6 mb-6">
      <h2 className="text-lg font-medium text-stone-900 mb-1 font-display">Business Logo</h2>
      <p className="text-sm text-stone-500 mb-4">
        Shown to visitors throughout the check-in flow. PNG, JPEG, WebP or SVG, up to 2 MB and 2000&times;2000 pixels.
      </p>

      <div className="flex items-center gap-5">
        {logoUrl ? (
          <img
            src={logoUrl}
            alt="Business logo"
            className="w-20 h-20 object-contain rounded-lg border border-stone-200 bg-stone-50"
          />
        ) : (
          <div className="w-20 h-20 rounded-lg border-2 border-dashed border-stone-300 flex items-center justify-center text-stone-400 text-xs">
            No logo
          </div>
        )}

        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_LOGO_TYPES}
            onChange={handleUpload}
            disabled={busy}
            className="hidden"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={busy}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-stone-300 text-sm font-medium text-stone-700 rounded-lg hover:bg-stone-50 disabled:opacity-50 transition-colors"
          >
            {busy && <Loader2 size={15} className="animate-spin" />}
            {logoUrl ? "Replace logo" : "Upload logo"}
          </button>
          {logoUrl && (
            <button
              type="button"
              onClick={handleRemove}
              disabled={busy}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-stone-500 rounded-lg hover:bg-stone-100 hover:text-red-600 disabled:opacity-50 transition-colors"
            >
              <Trash2 size={15} />
              Remove
            </button>
          )}
        </div>
      </div>

      {error && <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg mt-4">{error}</p>}
    </div>
  );
}

function BrandingUpgradeGate() {
  return (
    <div className="bg-gradient-to-br from-brand-50 to-white border border-brand-100 rounded-2xl p-8 text-center mb-6">
      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-brand-100 mb-4">
        <Sparkles size={22} className="text-brand-600" />
      </div>
      <h2 className="text-lg font-semibold text-stone-900 mb-2">Custom branding is a Professional feature</h2>
      <p className="text-sm text-stone-600 max-w-md mx-auto mb-5">
        Upgrade to put your own logo on the visitor check-in screens, so visitors see your business rather than a
        generic form. Custom logo and branding unlock on the Professional plan and above.
      </p>
      <Link
        to={workspacePath("billing")}
        className="inline-flex items-center gap-2 px-5 py-2.5 bg-brand-500 text-white text-sm font-medium rounded-lg hover:bg-brand-700 transition-colors"
      >
        <Sparkles size={16} />
        Upgrade to enable
      </Link>
    </div>
  );
}
